import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, SkeletonText } from '@chakra-ui/react';

import Carousel from '../component/Carousel';
import InfiniteScrollWrapper from 'src/component/common/InfiniteScrollWrapper/InfiniteScrollWrapper';
import SearchInput from 'src/component/common/SearchInput/SearchInput';
import VideosByCategory from './VideosByCategory';

import { GetAllVideosByCategories, getTrendingVideos } from 'src/redux/actions';
import { useUser } from 'src/hooks/useUser';
import CardLoader from 'src/component/CardLoader';
import { isMobile } from 'src/component/CourseDetail/Practice/Util';
import TendingVideos from 'src/component/TendingVideos/TendingVideos';

const Explore = React.memo(() => {
  const dispatch = useDispatch();
  let NoOfCards = isMobile ? 1 : 4;
  const { user: auth, isAdmin } = useUser();

  const categories = useSelector((state) => state.mainReducer.videoCategories);
  const trendingVideos = useSelector((state) => state.mainReducer.trendingVideos);

  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAllCategoriesView, setShowAllCategoriesView] = useState(true);

  const filteredCategories = useMemo(
    () =>
      categories?.filter(
        ({ videos }) => Array.isArray(videos) && videos.length > 0
      ) || [],
    [categories]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [searchText]);

  // fetch trending videos api
  useEffect(() => {
    const fetchTrendingVideos = async () => {
      try {
        await dispatch(getTrendingVideos(20));;
      } catch (error) {
        console.log("latest videos error", error);
      }
    }
    fetchTrendingVideos();
  }, [dispatch]);


  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await dispatch(GetAllVideosByCategories({ page, searchText }));
        const { hasMore: hasMoreFromApi } = res?.results || {};
        setHasMore(hasMoreFromApi);
      } catch (err) {
        console.error('Error fetching videos:', err);
      } finally {
        setLoading(false);
      }
    };
    if (showAllCategoriesView) {
      fetchVideos();
    }
  }, [dispatch, page, searchText, showAllCategoriesView]);


  useEffect(() => {
    setLoading(true);
    setPage(1);
  }, [searchText, showAllCategoriesView]);

  const fetchNextPage = () => {
    setPage((prev) => prev + 1);
  };

  const handleSeeAllClick = (category, isShowAll) => {
    if (category?.videos?.length > 0) {
      setSelectedCategory(category);
      setShowAllCategoriesView(isShowAll);
    }
  };

  return (
    <Box p={4} width="100%">
      <Box
        width="100%"
        position="sticky"
        top="65px"
        zIndex="1000"
        bg="white"
        paddingBottom="10px"
      >
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Box width="100%" mt="16px" maxW="600px">
            <SearchInput searchText={searchText} setSearchText={setSearchText} />
          </Box>
        </Box>
      </Box>

      <Stack position="relative" overflow="hidden" width="100%">
        {showAllCategoriesView ? (
          loading ? (
            // loading indicator
            <Box>
              <Box mb={4}>
                <SkeletonText noOfLines={1} maxW="150px" borderRadius="md" />
              </Box>
              <Box display="flex" flexWrap="wrap" gap="16px">
                {[...Array(NoOfCards)].map((_, i) => (
                  <CardLoader key={i} height="169px" maxW="500px" />
                ))}
              </Box>
              <Box mb={4} mt={10}>
                <SkeletonText noOfLines={1} maxW="150px" borderRadius="md" />
              </Box>
              <Box display="flex" flexWrap="wrap" gap="16px">
                {[...Array(NoOfCards)].map((_, i) => (
                  <CardLoader key={i} height="169px" maxW="500px" />
                ))}
              </Box>
            </Box>
          ) :
            <>
              {/* Trending Videos List */}
              {trendingVideos?.length > 0 &&
                <TendingVideos
                  videos={trendingVideos}
                  auth={auth}
                  isAdmin={isAdmin}
                  showAllCategoriesView={showAllCategoriesView}
                  handleSeeAllClick={handleSeeAllClick}
                />
              }
              {/* Category Videos List */}
              {filteredCategories?.length > 0 ? (
                <InfiniteScrollWrapper
                  dataLength={categories?.length || 0}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasMore}
                >
                  {filteredCategories.map((category, index) => (
                    <Carousel
                      key={`video-${index}`}
                      auth={auth}
                      isAdmin={isAdmin}
                      identifier="slug"
                      navUrl="video"
                      showCardMetaData={false}
                      pageName="explore"
                      courseKey={category}
                      title={category.categoryName}
                      data={category.videos}
                      hasStartButton={false}
                      showProgress
                      showAllCourse={showAllCategoriesView}
                      seeAllBtnFn={handleSeeAllClick}
                      cardWidth="300px"
                      cardWHeight="169px"
                      cardRatio="16/9"
                    />
                  ))}
                </InfiniteScrollWrapper>
              ) : (
                <Box textAlign="center" py={10} color="gray.500" fontSize="lg">
                  No videos available.
                </Box>
              )}
            </>
        ) : (
          <VideosByCategory
            category={selectedCategory}
            showAllCourse={showAllCategoriesView}
            setShowAllCourse={setShowAllCategoriesView}
            searchText={searchText}
            setSearchText={setSearchText}
          />
        )}

      </Stack>
    </Box>
  );
});

Explore.displayName = 'Explore';
export default Explore;
